import React, { useMemo } from "react";
import RegisterHero from "../components/RegisterHero";
import {
  Section,
  Container,
  Heading,
  Card,
  CardInner,
  Button,
} from "toddlar-ui";
import {
  useRegister,
  type RegisterPlan,
  type BillingPeriod,
} from "../state/register";
import { useNavigate } from "react-router-dom";

// BillingPeriod type imported from register state

const periods: { key: BillingPeriod; label: string }[] = [
  { key: "monthly", label: "Monthly" },
  { key: "6m", label: "6 months" },
  { key: "yearly", label: "Yearly" },
];

const plans: RegisterPlan[] = [
  {
    id: "sack",
    name: "Toy Sack",
    points: 800,
    prices: { monthly: 189, "6m": 999, yearly: 1999 },
    features: ["Great for starters"],
  },
  {
    id: "box",
    name: "Toy Box",
    points: 1000,
    prices: { monthly: 239, "6m": 1299, yearly: 2499 },
    features: ["Most popular"],
  },
  {
    id: "chest",
    name: "Toy Chest",
    points: 1400,
    prices: { monthly: 299, "6m": 1599, yearly: 2999 },
    features: ["Best value"],
  },
];

const Tick: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex items-start gap-2 text-ink min-w-1/2">
    <span className="text-green-600">✓</span>
    <span>{children}</span>
  </div>
);

const Register: React.FC = () => {
  const navigate = useNavigate();
  const { billingPeriod, setBillingPeriod, selectedPlan, setSelectedPlan } =
    useRegister();

  const priceFor = (p: RegisterPlan) => p.prices[billingPeriod];

  const ctaDisabled = useMemo(() => !selectedPlan, [selectedPlan]);

  const handleContinue = () => {
    if (!selectedPlan) return;
    navigate("/signup");
  };

  return (
    <div className="min-h-screen bg-sand-50">
      <RegisterHero />

      <Section>
        <Container>
          <Heading size="h2" className="text-ink mb-4">
            1. Choose your billing period
          </Heading>
          <div className="inline-flex rounded-md border border-sand-200 overflow-hidden">
            {periods.map((p) => (
              <button
                key={p.key}
                onClick={() => setBillingPeriod(p.key)}
                className={`px-4 py-2 text-sm font-medium ${
                  billingPeriod === p.key
                    ? "bg-brand-600 text-white"
                    : "bg-white text-ink hover:bg-sand-50"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </Container>
      </Section>

      <Section>
        <Container>
          <Heading size="h2" className="text-ink mb-4">
            2. Choose a plan that suits you
          </Heading>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {plans.map((p) => (
              <div
                key={p.id}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedPlan(p)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") setSelectedPlan(p);
                }}
                className="focus:outline-none"
              >
                <Card
                  className={`cursor-pointer transition hover:shadow-lg ${
                    selectedPlan?.id === p.id ? "ring-4 ring-brand-500" : ""
                  }`}
                >
                  <CardInner>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-lg font-semibold text-ink">
                          {p.name}
                        </div>
                        <div className="text-sm text-ink-muted">
                          {p.points} points
                        </div>
                      </div>
                      <div className="text-xl font-bold text-brand-700">
                        {priceFor(p)}kr
                      </div>
                    </div>
                    {p.features?.length ? (
                      <ul className="mt-3 list-disc list-inside text-sm text-ink-muted">
                        {p.features.map((f) => (
                          <li key={f}>{f}</li>
                        ))}
                      </ul>
                    ) : null}
                  </CardInner>
                </Card>
              </div>
            ))}
          </div>

          <div className="mt-6">
            <Button
              size="large"
              disabled={ctaDisabled}
              onClick={handleContinue}
            >
              Continue
            </Button>
            <div className="flex flex-col gap-3 mt-6">
              <Tick>First delivery is FREE!</Tick>
              <Tick>Change / cancel your plan any time</Tick>
              <Tick>Clean, complete & ready to play</Tick>
              <Tick>14 Day Money Back Guarantee</Tick>
            </div>
          </div>
        </Container>
      </Section>
    </div>
  );
};

export default Register;
